import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { purchaseOrderApi, vendorApi, productApi, installerApi } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, CalendarIcon, Truck, Package, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

export default function CreatePO() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    vendor_id: '',
    delivery_type: 'delivery',
    installer_id: '',
    delivery_date: null,
    notes: '',
    notify_vendor: false,
  });
  
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendorsRes, productsRes, installersRes] = await Promise.all([
          vendorApi.getAll(),
          productApi.getAll(),
          installerApi.getAll()
        ]);
        setVendors(vendorsRes.data);
        setProducts(productsRes.data);
        setInstallers(installersRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const selectedVendor = vendors.find(v => v.id === formData.vendor_id);
  const vendorProducts = products.filter(p => p.vendor_id === formData.vendor_id);

  const handleAddItem = () => {
    setItems([...items, { id: crypto.randomUUID(), product_id: '', quantity: 1 }]);
  };

  const handleRemoveItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const getProduct = (productId) => products.find(p => p.id === productId);

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const product = getProduct(item.product_id);
      if (product) {
        return sum + (product.unit_price * item.quantity);
      }
      return sum;
    }, 0);
  };

  const handleSubmit = async (e, asDraft = true) => {
    e.preventDefault();
    
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    const invalidItems = items.filter(i => !i.product_id || i.quantity < 1);
    if (invalidItems.length > 0) {
      toast.error('Please complete all item details');
      return;
    }

    if (formData.delivery_type === 'pickup' && !formData.installer_id) {
      toast.error('Please select an installer for pickup');
      return;
    }

    setSubmitting(true);
    
    const selectedInstaller = installers.find(i => i.id === formData.installer_id);
    
    const poData = {
      vendor_id: formData.vendor_id,
      vendor_name: selectedVendor.name,
      items: items.map(item => {
        const product = getProduct(item.product_id);
        return {
          product_id: item.product_id,
          product_name: product.name,
          quantity: parseInt(item.quantity),
          unit_price: product.unit_price,
        };
      }),
      delivery_type: formData.delivery_type,
      installer_id: formData.installer_id || null,
      installer_name: selectedInstaller?.name || null,
      delivery_date: formData.delivery_date ? format(formData.delivery_date, 'yyyy-MM-dd') : null,
      notes: formData.notes || null,
    };

    try {
      const response = await purchaseOrderApi.create(poData, formData.notify_vendor);
      
      if (!asDraft) {
        await purchaseOrderApi.updateStatus(response.data.id, { status: 'pending' });
      }
      
      toast.success(asDraft ? 'Draft saved successfully' : 'Order submitted for approval');
      if (formData.notify_vendor && selectedVendor?.emails?.length > 0) {
        toast.info(`Vendor notification sent to ${selectedVendor.emails.join(', ')}`);
      }
      navigate('/purchase-orders');
    } catch (error) {
      console.error('Error creating PO:', error);
      toast.error('Failed to create purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-po-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-sm">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold font-heading">Create Purchase Order</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create a new order for your vendors
          </p>
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, true)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vendor Selection */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Vendor</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <Select 
                  value={formData.vendor_id} 
                  onValueChange={(value) => {
                    setFormData({ ...formData, vendor_id: value });
                    setItems([]);
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="select-po-vendor">
                    <SelectValue placeholder="Select a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        <div className="flex flex-col">
                          <span>{vendor.name}</span>
                          <span className="text-xs text-muted-foreground font-mono-data">ABN: {vendor.abn}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-heading">Order Items</CardTitle>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAddItem}
                    disabled={!formData.vendor_id}
                    className="rounded-sm"
                    data-testid="btn-add-item"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {!formData.vendor_id ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Please select a vendor first
                  </p>
                ) : vendorProducts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No products found for this vendor
                  </p>
                ) : items.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Click "Add Item" to add products to this order
                  </p>
                ) : (
                  <div className="space-y-4">
                    {items.map((item, index) => {
                      const product = getProduct(item.product_id);
                      return (
                        <div key={item.id} className="flex gap-4 items-start p-4 bg-muted/30 rounded-sm" data-testid={`po-item-${index}`}>
                          <div className="flex-1 grid grid-cols-2 gap-4">
                            <div className="col-span-2 md:col-span-1">
                              <Label className="text-xs">Product</Label>
                              <Select 
                                value={item.product_id}
                                onValueChange={(value) => handleItemChange(item.id, 'product_id', value)}
                              >
                                <SelectTrigger className="mt-1 rounded-sm" data-testid={`select-item-product-${index}`}>
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {vendorProducts.map(p => (
                                    <SelectItem key={p.id} value={p.id}>
                                      <div className="flex justify-between gap-4">
                                        <span>{p.name}</span>
                                        <span className="font-mono-data text-muted-foreground">
                                          ${p.unit_price.toFixed(2)}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-1 md:col-span-1">
                              <Label className="text-xs">Quantity</Label>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                className="mt-1 rounded-sm font-mono-data"
                                data-testid={`input-item-qty-${index}`}
                              />
                            </div>
                          </div>
                          <div className="text-right min-w-[100px]">
                            <Label className="text-xs">Subtotal</Label>
                            <p className="font-mono-data font-semibold mt-2">
                              ${product ? (product.unit_price * item.quantity).toFixed(2) : '0.00'}
                            </p>
                          </div>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-destructive mt-5"
                            data-testid={`btn-remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes or special instructions..."
                  className="rounded-sm"
                  rows={3}
                  data-testid="input-po-notes"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Delivery Details */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Delivery Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div>
                  <Label className="text-xs">Delivery Type</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={formData.delivery_type === 'delivery' ? 'default' : 'outline'}
                      className="flex-1 rounded-sm"
                      onClick={() => setFormData({ ...formData, delivery_type: 'delivery', installer_id: '' })}
                      data-testid="btn-delivery-type-delivery"
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Delivery
                    </Button>
                    <Button
                      type="button"
                      variant={formData.delivery_type === 'pickup' ? 'default' : 'outline'}
                      className="flex-1 rounded-sm"
                      onClick={() => setFormData({ ...formData, delivery_type: 'pickup' })}
                      data-testid="btn-delivery-type-pickup"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Pickup
                    </Button>
                  </div>
                </div>

                {formData.delivery_type === 'pickup' && (
                  <div>
                    <Label className="text-xs">Installer Picking Up</Label>
                    <Select 
                      value={formData.installer_id} 
                      onValueChange={(value) => setFormData({ ...formData, installer_id: value })}
                    >
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="select-installer">
                        <SelectValue placeholder="Select installer" />
                      </SelectTrigger>
                      <SelectContent>
                        {installers.map(installer => (
                          <SelectItem key={installer.id} value={installer.id}>
                            {installer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Expected Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full mt-1 rounded-sm justify-start text-left font-normal",
                          !formData.delivery_date && "text-muted-foreground"
                        )}
                        data-testid="btn-delivery-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.delivery_date ? format(formData.delivery_date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.delivery_date}
                        onSelect={(date) => setFormData({ ...formData, delivery_date: date })}
                        initialFocus
                        data-testid="calendar-delivery-date"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Items</span>
                  <span className="font-mono-data">{items.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Quantity</span>
                  <span className="font-mono-data">{items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0)}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="font-medium">Total</span>
                  <span className="font-mono-data font-semibold text-lg">
                    ${calculateTotal().toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                
                {selectedVendor?.emails?.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="notify_vendor" 
                        checked={formData.notify_vendor}
                        onCheckedChange={(checked) => setFormData({ ...formData, notify_vendor: checked })}
                        data-testid="checkbox-notify-vendor"
                      />
                      <Label htmlFor="notify_vendor" className="text-sm flex items-center gap-2 cursor-pointer">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        Notify vendor by email
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Will send to: {selectedVendor.emails.join(', ')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-2">
              <Button 
                type="button"
                className="w-full rounded-sm"
                onClick={(e) => handleSubmit(e, false)}
                disabled={submitting || items.length === 0}
                data-testid="btn-submit-po"
              >
                Submit for Approval
              </Button>
              <Button 
                type="submit"
                variant="outline"
                className="w-full rounded-sm"
                disabled={submitting || items.length === 0}
                data-testid="btn-save-draft"
              >
                Save as Draft
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
